class ComponentManager {
	builders = {};
	plugins = {};
	isReady = false;
	debug = true;
	csrfToken = null;

	constructor() {

		this.debug = document.querySelector('html').getAttribute('debug') !== 'false';
		this.csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

		if (document.readyState == 'complete') {
			this._ready();
		} else if (document.addEventListener) {
			document.addEventListener('DOMContentLoaded', this._ready.bind(this));
		} else if (document.attachEvent) {
			document.attachEvent("pluginsstatechange", this._ready.bind(this));
		} else {
			// TODO setInterval and check readyState == 'complete'
		}
	}

	log(message, ...more) {
		this._log('log', message, ...more);
	}

	warn(message, ...more) {
		this._log('warn', message, ...more);
	}

	error(message, ...more) {
		this._log('error', message, ...more);
	}

	_log(method, message, ...more) {

		if (this.debug) {
			console[method]('Component Manager - ' + message, ...more);
		}
	}

	_ready() {

		this.log('Document is ready!')

		this.isReady = true
		this.execute();
	}

	register(name, builder) {

		if (!builder instanceof Function) {
			builder = builder.default;
		}

		this.builders[name] = builder;

		this.log(`Component '${name}' registered`);

		if (this.isReady) {
			this.execute(name);
		}
	}

	plugin(name, callback) {

		this.plugins[name] = callback;

		this.log(`Plugin '${name}' registered`);

		if (this.isReady) {
			callback(document.body);
			this.log(`Plugin '${name}' executed`);
		}
	}

	execute(target = null, name = null) {

		if (!this.isReady) return;

		target = target ?? document.body;

		this.log(`Parsing <${target.tagName.toLowerCase()}>...`);

		const elements = target.querySelectorAll(
			(name ? `[data-component=${name}]` : '[data-component]') + ':not(.ready)'
		);

		this.log(`Found ${elements.length} components`)

		for (const element of Object.values(elements)) {
			for (const name of element.dataset.component.split(' ')) {
				this.build(name, element);
			}
		}

		if (!name) {
			for (const name in this.plugins) {
				this.plugins[name](target);
				this.log(`Plugin '${name}' executed`);
			}
		}
	}

	/**
	 *
	 * @param {String} name
	 * @param {Element} element
	 * @param {Object.<string, Element>|null} references
	 * @param {Object.<string, string>|null} attributes
	 * @param {Object.<string, string>|null} properties
	 */
	build(name, element, references = null, attributes = null, properties = null) {

		const builder = this.builders[name];

		if (element) {
			references = references ?? this.getReferences(element);
			attributes = attributes ?? this.getDataAttributes(element);
			properties = properties ?? this.getProperties(element);
		}

		if (builder) {

			const build = builder instanceof Function ? builder : builder.build;

			if (build) {

				try {
					build(element, references, attributes, properties);
				} catch (exception) {
					console.error(`Error during ${name} component`);
					console.error(exception);
				}

				if (element) {
					element.classList.add('ready');
				}

				this.log(`Component '${name}' built`);
			}

		} else {

			this.error(`Component '${name}' not found`);
		}
	}

	/**
	 * Retourne un dictionnaire de tous les éléments à l'intérieur de la composante ayant l'attribut [data-ref].
	 * La valeur de l'attribut sert de clé à l'intérieur de l'objet.
	 *
	 * @param component
	 * @return {Object.<string, Element>}
	 */
	getReferences(component) {

		const references = {};
		const elements = component.querySelectorAll('[data-ref]');

		for (const element of Object.values(elements)) {

			const current = references[element.dataset.ref];

			if (Array.isArray(current)) {
				references[element.dataset.ref].push(element);
			} else if (current) {
				references[element.dataset.ref] = [current, element];
			} else {
				references[element.dataset.ref] = element;
			}
		}

		return references;
	}

	/**
	 * Retourne un dictionnaire de tous les data attributs, à l'exception de [data-component] et [data-ref].
	 * La clé est le nom du data attribut sans le préfix [data-].
	 *
	 * @param component
	 * @return {Object.<string, string>}
	 */
	getDataAttributes(component) {

		/** @type {Object.<string, string>} */
		const data = Object.assign({}, component.dataset);

		delete data.component;
		delete data.ref;

		return data;
	}

	/**
	 * Retourne un objet créer à partir de la première balise script contenant du json.
	 *
	 * @param component
	 * @return {Object.<string, string>}
	 */
	getProperties(component) {

		const script = component.tagName === 'SCRIPT'
			? component
			: component.querySelector('script[type="application/json"]');
		try {
			return script ? JSON.parse(script.text) : {};
		} catch (ex) {
			console.error(ex);
		}
	}

	get(action, success = null, fail = null, then = null) {
		return this.ajax('GET', action, null, success, fail, then);
	}

	post(action, data, success = null, fail = null, then = null) {
		return this.ajax('POST', action, data, success, fail, then);
	}

	api(action, data, success = null, fail = null, then = null) {
		return this.ajax('API', action, data, success, fail, then);
	}

	ajax(method, action, data, success = null, fail = null, then = null) {

		const xhr = new XMLHttpRequest();

		xhr.withCredentials = true;


		if (method === 'API') {

			xhr.open('POST', action);
			xhr.setRequestHeader('Accept', 'application/json');

			if (!(data instanceof FormData)) {
				let formData = new FormData();
				for (const key in data) {
					formData.append(key, data[key]);
				}
				data = formData;
			}

		} else {

			xhr.open(method, action);
		}

		xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
		xhr.setRequestHeader('X-CSRF-Token', this.csrfToken);


		xhr.onreadystatechange = () => {

			if (xhr.readyState === XMLHttpRequest.DONE) {

				const response = xhr.getResponseHeader('content-type') == 'application/json'
					? JSON.parse(xhr.response)
					: xhr.response;

				if (xhr.status !== 0) {
					if (xhr.status < 400) {
						if (success) {
							success.bind(xhr)(response);
						}

						if (response.success) {
							this.popup('success', response.success);
						}

					} else {
						if (fail) {
							fail.bind(xhr)(response);
						}

						// Il faudrait documenter ces behaviours quelque part pourt pas qu'on tombe dedans par accident.
						if (response.error) {
							this.popup('error', response.error);

						} else if (response.message) {
							this.popup('error', response.message);
						}
					}

					if (then) {
						then.bind(xhr)(response);
					}
				}
			}
		};

		xhr.send(data);

		return xhr;
	}

	popup(type, message) {

		const properties = JSON.parse(document.querySelector('#popup-' + type).text);

		if (type === 'success') {
			properties.title = message;
		} else {
			properties.text = message;
		}

		/* data-attributes are always string so this is an accurate simulation */
		this.build('popup', null, null, { autoFire: '1' }, properties);
	}

	undo(element) {

		const elements = element.querySelectorAll('*[data-undo]');

		this._undo(element);

		for (const element of elements) {
			this._undo(element);
		}

		const errors = element.querySelectorAll('.form__field_error');

		for (const error of errors) {
			error.remove();
		}
	}

	_undo(element) {

		switch (element.tagName) {
			case 'INPUT':
				switch (element.type) {
					case 'checkbox':
					case 'radio':
						if (element.checked != element.dataset.undo) {
							element.checked = element.dataset.undo;
							element.dispatchEvent(new Event('change'));
						}
						break;
					case 'submit':
						break;
					default:
						if (element.value != element.dataset.undo) {
							element.value = element.dataset.undo;
							element.dispatchEvent(new Event('change'));
						}
						break;
				}
				break;
			case 'IMG':
				if (element.src != element.dataset.undo) {
					element.src = element.dataset.undo;
				}
				break;
			default:
				if (element.dataset.component == 'dropdown') {
					$(element).dropdown('set selected', element.dataset.undo);
				}
				break;
		}
	}
}

const componentManager = new ComponentManager();

export default componentManager;


